import { useAsyncData } from '#imports'
import { type Reactive, type Ref, computed } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { buildUiFilters } from '@backmarket/nuxt-layer-recommendation/filtering/buildUiFilters'
import { filtersToWhereClause } from '@backmarket/nuxt-layer-recommendation/filtering/filtersToWhereClause'
import { getGraphFacet } from '@backmarket/nuxt-layer-recommendation/filtering/getGraphFacet'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import algoliasearch from 'algoliasearch'

import {
  HITS_PER_PAGE,
  OUTLET_LANDING_CARDS,
  OUTLET_PRICE_FACET,
  type OutletCategorySlug,
} from '../constants'

import { useAlgoliaIndex } from './useAlgoliaIndex'

type OutletSearchData = {
  hits: AlgoliaProduct[]
  nbHits: number
  nbPages: number
  facets: Facet[]
  priceFacet: UiPriceFacet | undefined
}

const DEFAULT_DATA: OutletSearchData = {
  hits: [],
  nbHits: 0,
  nbPages: 0,
  facets: [],
  priceFacet: undefined,
}

export function useOutletSearch({
  categorySlug,
  page,
  filtersInput,
  priceInput,
}: {
  categorySlug: Ref<OutletCategorySlug>
  page: Ref<number>
  filtersInput: Reactive<Record<string, string[]>>
  priceInput: Ref<[number, number] | undefined>
}) {
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const { indexName } = useAlgoliaIndex()
  const locale = useI18nLocale()

  const category = computed(() =>
    OUTLET_LANDING_CARDS.find((c) => c.slug === categorySlug.value),
  )

  const baseFilter = computed(
    () =>
      `${category.value?.filter ?? ''}${category.value?.localeFilters?.[locale] ?? ''}`,
  )

  const filters = computed(() =>
    filtersToWhereClause(filtersInput, baseFilter.value),
  )

  const facetNames = computed(() => {
    const sidebar = category.value?.facets?.map(({ name }) => name) ?? []
    const priceBuckets = OUTLET_PRICE_FACET.scales.map(
      ({ facetName }) => facetName,
    )

    return [...sidebar, 'price', ...priceBuckets]
  })

  return useAsyncData(
    getAsyncDataKey('outlet-search', { slug: categorySlug.value }),
    async () => {
      const algoliaClient = algoliasearch('dummy_app_id', 'dummy_key', {
        ...algoliaSearchOption,
      })

      const numericFilters = priceInput.value
        ? [`price>=${priceInput.value[0]}`, `price<=${priceInput.value[1]}`]
        : undefined

      return algoliaClient.initIndex(indexName).search<AlgoliaProduct>('', {
        filters: filters.value,
        facets: facetNames.value,
        numericFilters,
        page: page.value - 1,
        hitsPerPage: HITS_PER_PAGE,
      })
    },
    {
      watch: [filters, page, priceInput],
      default: () => DEFAULT_DATA,
      transform: (result): OutletSearchData => {
        if (!result) return DEFAULT_DATA

        return {
          hits: result.hits ?? [],
          nbHits: result.nbHits ?? 0,
          nbPages: result.nbPages ?? 0,
          facets: buildUiFilters(
            [],
            category.value?.facets ?? [],
            result.facets,
          ),
          priceFacet:
            result.facets &&
            getGraphFacet(OUTLET_PRICE_FACET, result.facets, {
              avg: result.facets_stats?.price.avg ?? 0,
              max: result.facets_stats?.price.max ?? 0,
            }),
        }
      },
    },
  )
}
